import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query, where, deleteDoc, doc } from 'firebase/firestore';
import { Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Grid, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { QRCodeCanvas } from 'qrcode.react';

function makeRandomId(len = 8) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < len; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
  return out;
}

const PreGeneratedQRCodes = () => {
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState(0);
  const [newQrs, setNewQrs] = useState([]);
  const [linkedQrs, setLinkedQrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printTarget, setPrintTarget] = useState(null);

  useEffect(() => {
    if (!user) return;
    fetchLists();
    // eslint-disable-next-line
  }, [user]);

  const getTimestampValue = (value) => {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.getTime === 'function') return value.getTime();
    return 0;
  };

  const fetchLists = async () => {
    setLoading(true);
    const nq = query(collection(db, 'qrcodes'), where('linked', '==', false));
    const lq = query(collection(db, 'qrcodes'), where('linked', '==', true));
    const [nSnap, lSnap] = await Promise.all([getDocs(nq), getDocs(lq)]);

    const newQrsData = nSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    newQrsData.sort((a, b) => getTimestampValue(b.createdAt) - getTimestampValue(a.createdAt));

    const linkedQrsData = lSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    linkedQrsData.sort((a, b) => getTimestampValue(b.linkedAt) - getTimestampValue(a.linkedAt));

    setNewQrs(newQrsData);
    setLinkedQrs(linkedQrsData);
    setLoading(false);
    // Auto-generate if low
    if (newQrsData.length <= 30) {
      await generateQrs(50);
      // refetch
      const r = await getDocs(query(collection(db, 'qrcodes'), where('linked', '==', false)));
      const refreshed = r.docs.map(d => ({ id: d.id, ...d.data() }));
      refreshed.sort((a, b) => getTimestampValue(b.createdAt) - getTimestampValue(a.createdAt));
      setNewQrs(refreshed);
    }
  };

  const generateQrs = async (count = 50) => {
    const batch = [];
    for (let i = 0; i < count; i++) {
      const qrId = `QR${Date.now().toString(36)}${makeRandomId(4)}`;
      batch.push(addDoc(collection(db, 'qrcodes'), { qrId, createdAt: new Date(), linked: false }));
      // slightly delay id generation to avoid collisions
      await new Promise(r => setTimeout(r, 5));
    }
    await Promise.all(batch);
  };

  const handleGenerate = async () => {
    setLoading(true);
    await generateQrs(50);
    await fetchLists();
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, 'qrcodes', id));
    fetchLists();
  };

  const handlePrint = (qr) => {
    const url =
      `${window.location.origin}#/register?qr=${encodeURIComponent(qr.qrId)}`;

    const win = window.open("", "_blank", "width=500,height=700");

    win.document.write(`
    <html>
    <head>
      <title>QR Sticker</title>
      <style>
        @page{
          margin:5mm;
        }

        body{
          margin:0;
          padding:20px;
          text-align:center;
          font-family:Arial,sans-serif;
        }

        .card{
          width:80mm;
          margin:auto;
          border:1px solid #0b63f6;
          border-radius:10px;
          padding:15px;
        }

        img{
          width:140px;
          height:140px;
        }

        h1{
          color:#0b63f6;
          margin:5px;
        }
      </style>
    </head>
    <body>
      <div class="card">
        <h1>GadiMitra</h1>
        <h3>Vehicle Alert Sticker</h3>

        <img src="${document.querySelector("canvas").toDataURL()}">

        <h4>${qr.qrId}</h4>

        <p>
        Scan to register this vehicle or Sen.
        </p>
<p style="mt: 2, py: 1, px: 2, bgcolor: '#0b63f6', borderRadius: 2, color: '#fff', fontWeight: 700">
       Fast alerts for vehicle owners in Bihar
        </p>
        <p>
       Contact info@gadimitra.in
        </p>
        <p>
        +91-9955559152
        </p>
        
      </div>
    </body>
    </html>
  `);

    win.document.close();

    setTimeout(() => {
      win.print();
      win.close();
    }, 500);
  };
  useEffect(() => {
    if (!printTarget) return undefined;

    const timer = window.setTimeout(() => {
      window.print();
    }, 100);

    return () => window.clearTimeout(timer);
  }, [printTarget]);

  useEffect(() => {
    const clearPrint = () => setPrintTarget(null);
    window.addEventListener('afterprint', clearPrint);
    return () => window.removeEventListener('afterprint', clearPrint);
  }, []);

  if (!user) return <Typography>Not authorized</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h5">Pre-generated QR Codes</Typography>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={handleGenerate}>Generate 50 QR</Button>
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 2 }}>
        <Tab label={`New QR (${newQrs.length})`} />
        <Tab label={`Linked QR (${linkedQrs.length})`} />
      </Tabs>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {tab === 0 && !loading && (
        <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(13, 71, 161, 0.08)' }}>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', width: 100 }}>QR Code</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 140 }}>QR ID</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Created At</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 250 }}>Registration Link</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 280 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {newQrs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No pre-generated QR codes found. Click "Generate 50 QR" to create new codes.
                  </TableCell>
                </TableRow>
              ) : newQrs.map(q => {
                const registerUrl = `${window.location.origin}/app/#/register?qr=${encodeURIComponent(q.qrId)}`;
                return (
                  <TableRow key={q.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ whiteSpace: 'nowrap', width: 100 }}>
                      <QRCodeCanvas value={registerUrl} size={80} includeMargin />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 140 }}>{q.qrId}</TableCell>
                    <TableCell sx={{ minWidth: 200, wordWrap: 'break-word' }}>{q.createdAt?.toDate ? q.createdAt.toDate().toString() : String(q.createdAt)}</TableCell>
                    <TableCell sx={{ minWidth: 250, wordWrap: 'break-word', fontSize: '0.875rem' }}>{registerUrl}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 280, display: 'flex', gap: 0.5 }}>
                      <Button size="small" variant="outlined" onClick={() => navigator.clipboard.writeText(registerUrl)}>Copy</Button>
                      <Button size="small" variant="outlined" onClick={() => handlePrint(q)}>Print</Button>
                      <Button size="small" variant="outlined" color="error" onClick={() => handleDelete(q.id)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {tab === 1 && !loading && (
        <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(13, 71, 161, 0.08)' }}>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 140 }}>QR ID</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 150 }}>Linked Vehicle</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap', minWidth: 150 }}>Registration ID</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Linked At</TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 250 }}>Alert Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linkedQrs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No linked QR codes yet. Register a vehicle with a pre-generated QR to link it.
                  </TableCell>
                </TableRow>
              ) : linkedQrs.map(q => {
                const alertUrl = `${window.location.origin}/app/#/alert/qr/${encodeURIComponent(q.qrId)}`;
                return (
                  <TableRow key={q.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 140 }}>{q.qrId}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>{q.linkedVehicleNumber || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>{q.linkedRegistrationId || '-'}</TableCell>
                    <TableCell sx={{ minWidth: 200, wordWrap: 'break-word' }}>{q.linkedAt?.toDate ? q.linkedAt.toDate().toString() : String(q.linkedAt)}</TableCell>
                    <TableCell sx={{ minWidth: 250, wordWrap: 'break-word', fontSize: '0.875rem' }}>{alertUrl}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box id="qr-print-wrapper" sx={{ display: 'none' }}>
        <style>{`
          #qr-print-wrapper { display: none; }
          @media print {
            body * { visibility: hidden; }
            #qr-print-wrapper { display: block !important; }
            #qr-print-area, #qr-print-area * { visibility: visible; }
            #qr-print-area { display: block !important; position: absolute; left: 0; top: 0; width: 100%; min-width: 80mm; max-width: 90mm; padding: 18px; box-sizing: border-box; background: #f4f8fb; color: #111; border: 1px solid #0b63f6; border-radius: 12px; }
          }
        `}</style>
        {printTarget && (
          <Box id="qr-print-area" sx={{ p: 3, textAlign: 'center', bgcolor: '#f4f8fb', border: '1px solid #0b63f6', borderRadius: 2, width: '100%', maxWidth: 400, mx: 'auto' }}>
            <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 800, color: '#0b63f6' }}>
              GadiMitra
            </Typography>
            <Typography variant="subtitle1" component="div" sx={{ mb: 2, fontWeight: 600 }}>
              Vehicle Alert Sticker
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: '#333', px: 1 }}>
              Scan to register this vehicle and receive instant owner alerts for parking, emergencies, and lost items.
            </Typography>
            <Box sx={{ display: 'inline-block', bgcolor: '#fff', p: 2, borderRadius: 3, mb: 2, boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }}>
              <QRCodeCanvas value={`${window.location.origin}/app/#/register?qr=${encodeURIComponent(printTarget.qrId)}`} size={180} includeMargin />
            </Box>
            <Typography variant="body2" sx={{ mt: 1, mb: 2, fontWeight: 600 }}>
              {printTarget.qrId}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word', fontSize: '0.85rem', mb: 1, px: 1 }}>
              {`${window.location.origin}/app/#/register?qr=${printTarget.qrId}`}
            </Typography>
            <Box sx={{ mt: 2, py: 1, px: 2, bgcolor: '#0b63f6', borderRadius: 2, color: '#fff' }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Fast alerts for vehicle owners in Bihar</Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 2, color: '#333' }}>
              Contact: support@gadimitra.in
            </Typography>
            <Typography variant="body2" sx={{ color: '#333' }}>
              +91-9955559152
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PreGeneratedQRCodes;
